import { useCallback, useEffect, useState } from "react";
import api, { API_URL } from "@/lib/api";
import { fmtL, fmtDate, fmtDateTime, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PaperclipIcon, XIcon, LockOpenIcon, NotebookIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { getToken } from "@/lib/token";

/**
 * Bitácora de cobros y compromisos de pago del cliente (solo agregar: nada se borra ni se reescribe).
 * Solicitud de desbloqueo (motivo, observaciones, soportes PDF/imagen, promesa de pago) y, para el
 * Administrador, aprobación con condición explícita, rechazo, revocación y levantamiento de rebloqueo.
 */
const TIPO = {
  gestion: "Gestión de cobro", correccion: "Corrección", solicitud: "Solicitud de desbloqueo", promesa: "Promesa de pago",
  decision: "Decisión del Administrador", pago: "Pago", cumplimiento: "Cumplimiento", vencimiento: "Vencimiento",
  rebloqueo: "Rebloqueo automático", revocacion: "Revocación", operacion: "Operación autorizada", reactivacion: "Reactivación",
};
const ESTADO_CLS = {
  pendiente: "border-agro-yellow/40 text-agro-yellow", aprobada: "border-agro-green/40 text-agro-green",
  rechazada: "border-red-500/40 text-red-300", activa: "border-agro-green/40 text-agro-green",
  consumida: "border-border text-text-secondary", cumplida: "border-agro-green/40 text-agro-green",
  vencida: "border-red-500/40 text-red-300", revocada: "border-red-500/40 text-red-300", en_uso: "border-agro-yellow/40 text-agro-yellow",
};

async function verArchivo(id) {
  try {
    const res = await fetch(`${API_URL}/credit-requests/files/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error("Sin acceso al archivo");
    window.open(URL.createObjectURL(await res.blob()), "_blank", "noopener");
  } catch (e) { toast.error(e.message); }
}

export default function CreditLogTab({ cid, client, balance, onReload, abrirSolicitud, onSolicitudCerrada }) {
  const { user } = useAuth();
  const esAdmin = Boolean(user?.effective?.is_admin || user?.role === "admin");
  const [data, setData] = useState(null);
  const [texto, setTexto] = useState("");
  const [modal, setModal] = useState(false);
  const [decidir, setDecidir] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get(`/clients/${cid}/bitacora`)).data); } catch (e) { toast.error(apiErr(e)); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (abrirSolicitud) setModal(true); }, [abrirSolicitud]);

  const agregar = async () => {
    if (!texto.trim()) return;
    try {
      await api.post(`/clients/${cid}/bitacora`, { tipo: "gestion", texto });
      setTexto(""); toast.success("Gestión registrada"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const corregir = async (e) => {
    const t = window.prompt("Corrección (queda como una entrada nueva vinculada a la original):");
    if (!t) return;
    try { await api.post(`/clients/${cid}/bitacora`, { tipo: "correccion", texto: t, corrige_id: e.id }); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const accionAdmin = async (url, pregunta) => {
    const motivo = window.prompt(pregunta);
    if (motivo === null) return;
    try { await api.post(url, { motivo }); toast.success("Registrado"); load(); onReload?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const pendiente = (data?.solicitudes || []).find((s) => s.estado === "pendiente");
  return (
    <div className="space-y-4" data-testid="credit-log">
      <div className="flex flex-wrap gap-2 items-center">
        <NotebookIcon size={20} className="text-agro-green" />
        <h3 className="font-heading text-lg">Bitácora de cobros y compromisos de pago</h3>
        {balance?.bloqueado && <span className="text-[10px] uppercase px-2 py-0.5 rounded border border-red-500/40 text-red-300">Crédito bloqueado</span>}
        {data?.rebloqueo && <span className="text-[10px] uppercase px-2 py-0.5 rounded border border-red-500/40 text-red-300" data-testid="rebloqueo">
          Rebloqueo: {data.rebloqueo.motivo}</span>}
        {!pendiente && (
          <button onClick={() => setModal(true)} data-testid="btn-solicitar-desbloqueo"
            className="ml-auto flex items-center gap-2 px-3 py-2 text-sm border border-agro-yellow/50 text-agro-yellow rounded-md">
            <LockOpenIcon size={15} /> Solicitar desbloqueo al Administrador
          </button>
        )}
        {esAdmin && data?.rebloqueo && (
          <button onClick={() => accionAdmin(`/clients/${cid}/credit-block/clear`, "Motivo para levantar el rebloqueo:")}
            className="px-3 py-2 text-sm border border-border rounded-md">Levantar rebloqueo</button>
        )}
      </div>

      {/* Solicitudes y autorizaciones */}
      {(data?.solicitudes || []).length > 0 && (
        <div className="bg-card border border-border/60 rounded-lg overflow-x-auto">
          <table className="w-full text-xs" data-testid="solicitudes-credito">
            <thead className="bg-sidebar text-[10px] uppercase text-text-muted"><tr>
              <th className="p-2 text-left">Solicitud</th><th className="p-2 text-left">Motivo</th><th className="p-2 text-left">Promesa</th>
              <th className="p-2 text-left">Soportes</th><th className="p-2 text-left">Estado</th><th className="p-2 text-left">Condición / desbloqueo</th><th className="p-2"></th></tr></thead>
            <tbody>
              {data.solicitudes.map((s) => {
                const a = (data.autorizaciones || []).find((x) => x.id === s.autorizacion_id);
                return (
                  <tr key={s.id} className="border-t border-border/40 align-top">
                    <td className="p-2"><div className="font-mono">{s.numero}</div><div className="text-text-muted">{fmtDateTime(s.fecha)} · {s.solicitante}</div></td>
                    <td className="p-2">{s.motivo}{s.observaciones && <div className="text-text-muted">{s.observaciones}</div>}</td>
                    <td className="p-2">{s.promesa ? <>{fmtL(s.promesa.monto)} al {fmtDate(s.promesa.fecha)}
                      <div className="text-text-muted">{(s.promesa.documentos || []).map((d) => d.numero).join(", ")}</div></> : "—"}</td>
                    <td className="p-2">{(s.adjuntos || []).map((f) => (
                      <button key={f.id} onClick={() => verArchivo(f.id)} className="flex items-center gap-1 text-agro-green hover:underline">
                        <PaperclipIcon size={12} /> {f.nombre}</button>))}</td>
                    <td className="p-2"><span className={`px-2 py-0.5 rounded border uppercase text-[10px] ${ESTADO_CLS[s.estado] || ""}`}>{s.estado}</span>
                      {s.decision?.rechazo && <div className="text-red-300 mt-1">{s.decision.rechazo}</div>}</td>
                    <td className="p-2">{a ? <>
                      <div>{a.tipo === "una_operacion" ? "Una operación" : `Temporal ${a.dias} días`} · <span className={`px-1.5 rounded border uppercase text-[10px] ${ESTADO_CLS[a.estado] || ""}`}>{a.estado}</span></div>
                      <div className="text-text-muted">Del {fmtDate(a.inicio)} al {fmtDate(a.vence)} · {a.alcance}</div>
                      {a.compromiso?.monto ? <div className="text-text-muted">Compromiso {fmtL(a.compromiso.monto)}{a.compromiso.fecha ? ` al ${fmtDate(a.compromiso.fecha)}` : ""} · pagado {fmtL(a.pagado || 0)}</div> : null}
                      {a.operacion && <div className="text-text-muted">Usada en factura {a.operacion.numero}</div>}
                    </> : "—"}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {esAdmin && s.estado === "pendiente" && <>
                        <button onClick={() => setDecidir(s)} data-testid={`aprobar-${s.numero}`} className="text-agro-green hover:underline mr-2">Aprobar</button>
                        <button onClick={() => accionAdmin(`/credit-requests/${s.id}/rechazar`, "Motivo del rechazo:")} className="text-red-300 hover:underline">Rechazar</button>
                      </>}
                      {esAdmin && a && ["activa", "en_uso", "consumida"].includes(a.estado) && (
                        <button onClick={() => accionAdmin(`/credit-authorizations/${a.id}/revocar`, "Motivo de la revocación:")} className="text-red-300 hover:underline">Revocar</button>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Nueva gestión */}
      <div className="bg-card border border-border/60 rounded-lg p-3 flex gap-2">
        <input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Registrar gestión de cobro u observación…"
          data-testid="bitacora-texto" className="flex-1 bg-app border border-border rounded-md px-3 py-2 text-sm" />
        <button onClick={agregar} data-testid="bitacora-agregar" className="px-4 py-2 bg-agro-green text-black rounded-md text-sm font-medium">Agregar</button>
      </div>

      {/* Línea de tiempo */}
      <div className="bg-card border border-border/60 rounded-lg divide-y divide-border/40" data-testid="bitacora-entradas">
        {(data?.entradas || []).length === 0 && <div className="p-6 text-center text-text-muted text-sm">Sin registros todavía</div>}
        {(data?.entradas || []).map((e) => (
          <div key={e.id} className="p-3 text-sm flex gap-3">
            <div className="text-[11px] text-text-muted w-36 flex-shrink-0">{fmtDateTime(e.fecha)}<div>{e.usuario}</div></div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider text-agro-green">{TIPO[e.tipo] || e.tipo}</div>
              <div>{e.texto}</div>
              {(e.datos?.adjuntos || []).map((f) => (
                <button key={f.id} onClick={() => verArchivo(f.id)} className="text-xs text-agro-green hover:underline flex items-center gap-1"><PaperclipIcon size={12} /> {f.nombre}</button>))}
            </div>
            {["gestion", "solicitud", "promesa"].includes(e.tipo) && (
              <button onClick={() => corregir(e)} className="text-[11px] text-text-muted hover:text-agro-green self-start">Corregir</button>)}
          </div>
        ))}
      </div>

      {modal && <SolicitudModal cid={cid} client={client} onClose={() => { setModal(false); onSolicitudCerrada?.(); }}
                                onSaved={() => { setModal(false); onSolicitudCerrada?.(); load(); }} />}
      {decidir && <AprobarModal sol={decidir} onClose={() => setDecidir(null)} onSaved={() => { setDecidir(null); load(); onReload?.(); }} />}
    </div>
  );
}

function SolicitudModal({ cid, client, onClose, onSaved }) {
  const [f, setF] = useState({ motivo: "", observaciones: "", conPromesa: false, monto: "", fecha: "", docs: [] });
  const [adjuntos, setAdjuntos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/reports/statements/${cid}/facturas`).then(({ data }) =>
      setFacturas((data.grupos || []).flatMap((g) => g.facturas || []).filter((d) => d.saldo > 0))).catch(() => {});
  }, [cid]);
  const subir = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("El archivo supera 8 MB");
    const fd = new FormData(); fd.append("cliente_id", cid); fd.append("archivo", file);
    try {
      const { data } = await api.post("/credit-requests/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAdjuntos((a) => [...a, data]);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const enviar = async () => {
    if (!f.motivo.trim()) return toast.error("Escribe el motivo");
    setSaving(true);
    try {
      await api.post("/credit-requests", {
        cliente_id: cid, motivo: f.motivo, observaciones: f.observaciones, adjuntos: adjuntos.map((a) => a.id),
        promesa: f.conPromesa ? { monto: Number(f.monto), fecha: f.fecha,
          documentos: f.docs.map((id) => ({ tipo: (facturas.find((x) => x.id === id) || {}).tipo === "nota_debito" ? "nota_debito" : "factura", id })) } : null,
      });
      toast.success("Solicitud enviada al Administrador"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const inp = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm";
  const sm = useModalForm({ value: { f, a: adjuntos.length }, onClose: onClose, onSave: enviar, mensaje: "¿Está seguro de que desea enviar la solicitud de crédito?" });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" {...sm.overlayProps}>
      <div role="dialog" className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="solicitud-modal">
        <div className="p-4 border-b border-border flex justify-between">
          <div><h3 className="font-heading text-lg">Solicitud de desbloqueo de crédito</h3>
            <div className="text-xs text-text-muted">{client?.nombre} · la decisión corresponde al Administrador</div></div>
          <button aria-label="Cerrar" onClick={sm.cerrar}><XIcon size={20} /></button>
        </div>
        <form className="p-4 space-y-3" onSubmit={sm.guardar}>
          <div><label className="text-[10px] uppercase text-text-muted">Motivo *</label>
            <textarea rows={2} required value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} className={inp} data-testid="sol-motivo" /></div>
          <div><label className="text-[10px] uppercase text-text-muted">Observaciones de cobro</label>
            <textarea rows={2} value={f.observaciones} onChange={(e) => setF({ ...f, observaciones: e.target.value })} className={inp} data-testid="sol-obs" /></div>
          <div><label className="text-[10px] uppercase text-text-muted">Soportes (PDF o imagen, máx. 8 MB)</label>
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={(e) => { subir(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="sol-archivo" className="block text-xs mt-1" />
            {adjuntos.map((a) => <div key={a.id} className="text-xs text-agro-green flex items-center gap-1"><PaperclipIcon size={12} /> {a.nombre}</div>)}</div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.conPromesa} onChange={(e) => setF({ ...f, conPromesa: e.target.checked })} data-testid="sol-promesa" />
            Registrar promesa de pago</label>
          {f.conPromesa && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] uppercase text-text-muted">Importe *</label>
                <input type="number" min="0.01" step="0.01" required value={f.monto} onChange={(e) => setF({ ...f, monto: e.target.value })} className={inp} data-testid="sol-monto" /></div>
              <div><label className="text-[10px] uppercase text-text-muted">Fecha comprometida *</label>
                <input type="date" required value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} className={inp} data-testid="sol-fecha" /></div>
              <div className="col-span-2"><label className="text-[10px] uppercase text-text-muted">Documentos que cubre</label>
                <div className="max-h-32 overflow-y-auto border border-border rounded p-2 space-y-1">
                  {facturas.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={f.docs.includes(d.id)} onChange={(e) => setF({ ...f, docs: e.target.checked ? [...f.docs, d.id] : f.docs.filter((x) => x !== d.id) })} />
                      {d.tipo_label || "Factura"} {d.numero} · saldo {fmtL(d.saldo)}</label>))}
                </div></div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={sm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
            <button type="submit" disabled={(saving) || sm.saving} data-testid="sol-enviar" className="px-4 py-2 text-sm bg-agro-green text-black rounded-md font-medium disabled:opacity-50">Enviar solicitud</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AprobarModal({ sol, onClose, onSaved }) {
  const [f, setF] = useState({ condicion: "una_operacion", dias: 15, compromiso_monto: sol.promesa?.monto || "",
                                compromiso_fecha: sol.promesa?.fecha || "", alcance: "", observaciones: "" });
  const hoy = new Date();
  const vence = new Date(hoy.getTime() + (f.condicion === "temporal" ? Number(f.dias) : 7) * 86400000);
  const guardar = async () => {
    try {
      await api.post(`/credit-requests/${sol.id}/aprobar`, { ...f, dias: Number(f.dias),
        compromiso_monto: f.compromiso_monto === "" ? null : Number(f.compromiso_monto), compromiso_fecha: f.compromiso_fecha || null });
      toast.success("Solicitud aprobada"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const inp = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm";
  const am = useModalForm({ value: f, onClose: onClose, onSave: guardar });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" {...am.overlayProps}>
      <div role="dialog" className="bg-card border border-border rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="aprobar-modal">
        <div className="p-4 border-b border-border"><h3 className="font-heading text-lg">Aprobar {sol.numero}</h3>
          <div className="text-xs text-text-muted">No aumenta el límite de crédito ni quita otros controles del cliente.</div></div>
        <div className="p-4 space-y-3">
          <div><label className="text-[10px] uppercase text-text-muted">Condición *</label>
            <select value={f.condicion} onChange={(e) => setF({ ...f, condicion: e.target.value })} className={inp} data-testid="apr-condicion">
              <option value="una_operacion">Desbloqueo para una operación</option>
              <option value="temporal">Desbloqueo temporal</option>
            </select></div>
          {f.condicion === "temporal" && (
            <div><label className="text-[10px] uppercase text-text-muted">Plazo *</label>
              <select value={f.dias} onChange={(e) => setF({ ...f, dias: e.target.value })} className={inp} data-testid="apr-dias">
                {[7, 15, 30].map((d) => <option key={d} value={d}>{d} días</option>)}
              </select></div>)}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] uppercase text-text-muted">Compromiso exigido (L){f.condicion === "temporal" ? " *" : ""}</label>
              <input type="number" value={f.compromiso_monto} onChange={(e) => setF({ ...f, compromiso_monto: e.target.value })} className={inp} /></div>
            <div><label className="text-[10px] uppercase text-text-muted">Fecha del compromiso</label>
              <input type="date" value={f.compromiso_fecha} onChange={(e) => setF({ ...f, compromiso_fecha: e.target.value })} className={inp} /></div>
          </div>
          <div><label className="text-[10px] uppercase text-text-muted">Alcance</label>
            <input value={f.alcance} onChange={(e) => setF({ ...f, alcance: e.target.value })} className={inp} placeholder="p. ej. Pedido de fertilizante de la semana" /></div>
          <div><label className="text-[10px] uppercase text-text-muted">Observaciones</label>
            <textarea rows={2} value={f.observaciones} onChange={(e) => setF({ ...f, observaciones: e.target.value })} className={inp} /></div>
          <div className="text-xs bg-agro-green/5 border border-agro-green/30 rounded p-2">
            Inicio: hoy · Vence: {vence.toLocaleDateString("es-HN")}. {f.condicion === "una_operacion"
              ? "Permite una única operación a crédito; después la línea vuelve a quedar bloqueada."
              : "Si no se cumple el compromiso, el crédito se rebloquea automáticamente al vencer."}
          </div>
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={am.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
          <button disabled={am.saving} onClick={am.guardar} data-testid="apr-guardar" className="px-4 py-2 text-sm bg-agro-green text-black rounded-md font-medium">Aprobar</button>
        </div>
      </div>
    </div>
  );
}
